import { CardBody, Container, Modal, Popover } from "reactstrap";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import React from "react";

import { MobileDateRangePickerWrapper } from "@components/Common/MobileDateRangePickerWrapper";
import DateRangePickerWrapper from "@components/Common/DateRangePickerWrapper";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import HeaderComponent from "@components/VerticalLayout/HeaderComponent";
import { CustomFooter } from "@pages/connecs/CustomFooter";
import NoDataOverlay from "@pages/connecs/NoDataOverlay";
import PageTitle from "@components/Common/PageTitle";
import { Box, Tooltip } from "@mui/material";

import { StyledPopoverBody } from "../connecs/StyledPopoverBody";
import BreadcrumbOptions from "../Reporting/BreadcrumbOptions";
import uploadIcon from "../../assets/images/uploadIcon.svg";
import {
    downloadStoreReportCsv,
    getAdvancedPeriodStringParam,
} from "@helpers/general";

type TransactionRow = {
    Date: number;
    TotalPaiement: string;
    FraisHT: string;
    TVA: string;
    TotalTTC: string;
    Totaldeducte: string;
    ResteApayer: string;
};

function Facturation() {
    const { t } = useTranslation();
    const uidUserEntity = localStorage.getItem("Uid_Entitie_User") || "";

    const [rows, setRows] = React.useState<TransactionRow[]>([]);
    const [isPopoverOpened, setIsPopoverOpened] = React.useState(false);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const { periodSlice, advancedDate } = useSelector((state: any) => state);

    const toggleOpenedPopover = React.useCallback(() => {
        setIsPopoverOpened((prevState) => !prevState);
    }, []);

    const toggleOpeningModal = React.useCallback(() => {
        setIsModalOpened((prevState) => !prevState);
    }, []);

    const [currentPage, setCurrentPage] = React.useState(0);
    const pageSize = 10;
    const totalPages = Math.ceil(rows.length / pageSize);
    const columns: GridColDef[] = [
        { field: "Date", headerName: "Date", flex: 1 },
        { field: "TotalPaiement", headerName: "Total Paiement", flex: 1 },
        { field: "FraisHT", headerName: "Frais HT", flex: 1 },
        { field: "TVA", headerName: "TVA", flex: 1 },
        { field: "TotalTTC", headerName: "Total TTC", flex: 1 },
        { field: "Totaldeducte", headerName: "Total deducté", flex: 1 },
        { field: "ResteApayer", headerName: "Reste à payer", flex: 1 },
        {
            field: "actions",
            flex: 1,
            headerName: "Actions",
            headerClassName: "custom-header",
            renderCell: (params: GridRenderCellParams) => (
                <div>
                    <Tooltip title="Télécharger le rapport" arrow>
                        <button
                            type="button"
                            onClick={async () => {
                                await downloadStoreReportCsv(
                                    params.row.NomFacture,
                                    params.row.merchantaccount,
                                    true,
                                    params.row.storeReference
                                );
                            }}
                            aria-label="Download CSV"
                            style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                            }}
                        >
                            <img
                                src={uploadIcon}
                                height={30}
                                width={30}
                                alt="Download CSV"
                                style={{
                                    backgroundColor: "transparent",
                                    mixBlendMode: "multiply",
                                }}
                            />
                        </button>
                    </Tooltip>
                </div>
            ),
            minWidth: 100,
            sortable: false,
        },
    ];
    const handlePageChange = (newPage: number) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleToggleOpenedPopover = React.useCallback(() => {
        toggleOpenedPopover();
    }, [toggleOpenedPopover]);

    const handleToggleOpeningModal = React.useCallback(() => {
        toggleOpeningModal();
    }, [toggleOpeningModal]);

    async function getFacturationsList(uuidEntity: string) {
        try {
            const filterPeriod = getAdvancedPeriodStringParam(
                periodSlice,
                advancedDate
            );
            const encodedFilterPeriod = encodeURIComponent(filterPeriod);

            const response = await fetch(
                `${process.env.REACT_APP_ADMIN_ACCESS}/fetchFacturationDetails?uuidEntity=${uuidEntity}&filterPeriod=${encodedFilterPeriod}`
            );
            const data = await response.json();
            return data.map((item: any, index: number) => ({
                id: index + 1,
                Date: item.Date,
                TotalPaiement: item.TotalPaiement,
                FraisHT: item.FraisHT,
                TVA: item.TVA,
                TotalTTC: item.TotalTTC,
                Totaldeducte: item.Totaldeducte,
                ResteApayer: item.ResteApayer,
                NomFacture: item.NomFacture,
                merchantaccount: item.merchantaccount,
                storeReference: item.storeReference,
            }));
        } catch (error) {
            return [];
        }
    }

    React.useEffect(() => {
        const loadRows = async () => {
            const fetchedRows = await getFacturationsList(uidUserEntity);
            setRows(fetchedRows);
        };
        loadRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uidUserEntity, periodSlice, advancedDate]);
 
    return (
        <React.Fragment>
            <PageTitle title="Report SoftaveraPay" />
            <div className="">
                <BreadcrumbOptions
                    currentLink={[
                        {
                            item: t("SOFTAVERAPAY"),
                            link: `${process.env.REACT_APP_PORTAIL_API}`,
                        },
                        {
                            item: t(`${"Facturation"}`),
                            link: `${"Facturation"}`,
                        },
                        {
                            item: t(`${"Facturation"}`),
                            link: `${"Facturation"}`,
                        },
                    ]}
                />

                <Container fluid>
                    <HeaderComponent
                        title={t(
                            "This section allows you to view and download the monthly SoftaveraPay billing reports (processing fees excluding and including tax)."
                        )}
                    />
                    <CardBody
                        style={{
                            padding: "1px",
                            backgroundColor: "white",
                            marginTop: "10px",
                        }}
                    >
                        <div>
                            <div className="transaction-overview">
                                <div className="filter-buttons">
                                    <button
                                        id="date-range-picker-popover"
                                        type="button"
                                        className="filter-button active"
                                        onClick={handleToggleOpenedPopover}
                                    >
                                        {t(`${periodSlice.date}`)}
                                    </button>
                                    {isPopoverOpened &&
                                        (isMobile === false ? (
                                            <Popover
                                                placement="top"
                                                isOpen={isPopoverOpened}
                                                target="date-range-picker-popover"
                                                toggle={toggleOpenedPopover}
                                                trigger="legacy"
                                            >
                                                <StyledPopoverBody>
                                                    <DateRangePickerWrapper
                                                        toggleOpenedPopover={
                                                            toggleOpenedPopover
                                                        }
                                                        isAdvancedReporting={
                                                            false
                                                        }
                                                    />
                                                </StyledPopoverBody>
                                            </Popover>
                                        ) : (
                                            <Modal
                                                isOpen={isModalOpened}
                                                toggle={
                                                    handleToggleOpeningModal
                                                }
                                                className="defined-range-modal__clz"
                                                centered
                                                fade={false}
                                            >
                                                <MobileDateRangePickerWrapper
                                                    toggleOpeningModal={
                                                        handleToggleOpeningModal
                                                    }
                                                />
                                            </Modal>
                                        ))}

                                    <button
                                        className="filter-button d-none"
                                        type="button"
                                    >
                                        Libellé
                                    </button>
                                </div>
                            </div>
                            <Box sx={{ height: 500, width: "100%" }}>
                                <DataGrid
                                    rows={rows.slice(
                                        currentPage * pageSize,
                                        (currentPage + 1) * pageSize
                                    )}
                                    columns={columns}
                                    disableSelectionOnClick
                                    components={{
                                        NoRowsOverlay: NoDataOverlay,
                                        Footer: CustomFooter,
                                    }}
                                    componentsProps={{
                                        noRowsOverlay: NoDataOverlay,
                                        footer: {
                                            currentPage,
                                            onPageChange: handlePageChange,
                                            totalPages,
                                        },
                                    }}
                                />
                            </Box>
                        </div>
                    </CardBody>
                </Container>
            </div>
        </React.Fragment>
    );
}

export default Facturation;
